import { useTranslation } from "react-i18next";
import DataTable from "../../components/carbon/DataTable/DataTable";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import { OfferResponse, OrderState, SupplierRfqActivity } from "../../types";
import { useUser } from "../../context";
import { buildOfferRows, offerListHeaders } from "./offerRows";
import "./bid.scss";

type OffersOnOrdersProps = {
  offOnOrd?: OfferResponse[];
  handleItemClick: (offer: OfferResponse) => void;
  state: OrderState;
  /** Whose validation the table shows, matched to the rows by email. */
  activity?: SupplierRfqActivity[];
};

/**
 * The buyer's own offers table for a SIMPLE order (Task 7) — Carbon `DataTable` in place of the
 * pre-Carbon `<ul>` of `OfferItem` cards. The buyer sees every offer on their own order (already
 * company-scoped server-side); this just changes how that same list renders. Row mapping lives in
 * `offerRows.tsx` (unit-tested) so the OPEN + compliant-buyer price-hiding rule isn't re-derived
 * inside JSX.
 */
const OffersOnOrders = ({
  offOnOrd,
  handleItemClick,
  state,
  activity,
}: OffersOnOrdersProps) => {
  const { isCompliant } = useUser();
  const { t } = useTranslation();

  // Same gate `OfferItem.tsx`'s `stateAndCompliance` used: while bidding is OPEN, a compliant
  // buyer sees supplier names only — no price/comment/attachment/status — plus the notice below.
  const hideOfferDetails = state === OrderState.OPEN && isCompliant;

  const rows = buildOfferRows(offOnOrd, hideOfferDetails, t, activity);
  const headers = offerListHeaders(t);

  return (
    <div className="bid-offers-table">
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={false}
        onRowClick={(row) => handleItemClick(row.offer)}
        empty={<EmptyState title={`${t("tenders.data.noOffers")}.`} />}
      />
      {hideOfferDetails && rows.length > 0 && (
        <p className="bid-offers-table__notice">
          {t("tenders.data.visualize")}
        </p>
      )}
    </div>
  );
};

export default OffersOnOrders;
